'use client';

import { useEffect, useState } from 'react';
import { useUnit } from 'effector-react';
import { openModalEvent } from '@nx-frontend/shared/ui/modal';
import { openImageViewerEvent } from '@nx-frontend/shared/store/image-viewer';
import { openPdfViewerEvent } from '@nx-frontend/shared/store/pdf-viewer';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import {
  BucketFile,
  FileType,
  PopupType,
  PortalStyleConfig,
  ThemeColorSchemas,
  UnleashFeatures,
} from '@nx-frontend/shared/types';
import { prepareFileItemUrl } from '@nx-frontend/shared/utils';

interface UseShowFileInfoParams {
  item: BucketFile;
  orgRole: string | undefined;
  fileOpenHost: string;
  userId?: number;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  openFileLocationEnabled: boolean;
  canEditOtherUsersFile: boolean;
  deleteDisabled?: boolean;
  renameDisabled?: boolean;
}

export const useShowFileInfo = (): ((params: UseShowFileInfoParams) => void) => {
  const [openImageViewer, openModal, openPdfViewer, unleashFlags] = useUnit([
    openImageViewerEvent,
    openModalEvent,
    openPdfViewerEvent,
    $unleashFlags,
  ]);
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [setIsMobile]);

  return (params: UseShowFileInfoParams) => {
    const {
      item,
      orgRole,
      userId,
      themeColors,
      themeStyle,
      openFileLocationEnabled,
      canEditOtherUsersFile,
      deleteDisabled,
      renameDisabled,
    } = params;

    if (item.type === FileType.IMAGE || item.format === 'webp' || item.format === 'svg') {
      openImageViewer({
        src: item.url,
        title: item.filename,
      });
    } else if (item.format === 'pdf' && unleashFlags[UnleashFeatures.PDF_VIEWER]) {
      const itemUrl = prepareFileItemUrl(item.url, params.fileOpenHost);
      if (isMobile) {
        /**
         * Download pdf if mobile.
         */
        if (itemUrl) {
          const name = itemUrl.split('/');
          const link = document.createElement('a');
          link.setAttribute('href', itemUrl);
          link.setAttribute('download', name[name.length - 1] ?? 'file.pdf');
          link.setAttribute('target', '_blank');
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        openPdfViewer({
          fileUrl: itemUrl,
          fileName: item.filename,
          isOpen: true,
        });
      }
    } else {
      openModal({
        content: PopupType.FILE_INFO,
        showCloseButton: false,
        modalContainerStyles: 'w-[795px]',
        payload: {
          payloadType: PopupType.FILE_INFO,
          themeColors,
          themeStyle,
          fileId: item.globalId,
          fileOpenHost: params.fileOpenHost,
          orgRole,
          userId,
          openFileLocationEnabled,
          canEditOtherUsersFile,
          renameDisabled,
          deleteDisabled,
        },
      });
    }
  };
};
